import $axios, { refreshSession } from '~/helpers/axios';
import { $setStorage } from '~/helpers/storage';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/vue';

let getMePromise = null;

export const useAuthStore = defineStore('auth', {
	state: () => ({
		token: null,
		version: null,
		permission: null,
		role: null,
		companies: null,
		company: null,
		branches: null,
		branch: null,
		division: null,
		documentations: null,
		data: null,
		errorLogin: [],
	}),

	actions: {
		resendVerification(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/verify/resend', payload)
					.then((res) => {
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},
		login(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/login', payload)
					.then((res) => {
						if (res.data.success === true) this.setSession(res);
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},
		loginAsUser(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/login-as-user', payload)
					.then((res) => {
						if (res.data.success === true) {
							this.setSession(res);
						}
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},

		register(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/register', payload)
					.then((res) => {
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},

		requestPassword(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/passwords/forgot', payload)
					.then((res) => {
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},

		changePassword(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/passwords/update', payload)
					.then((res) => {
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},

		resetPassword(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/passwords/reset', payload)
					.then((res) => {
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},

		validateToken(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/passwords/validate', payload)
					.then((res) => {
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},

		validateVerifyToken(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/verify', payload)
					.then((res) => {
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},

		activateAccount(payload) {
			return new Promise((resolve, reject) => {
				$axios
					.post('/auths/verify/activate', payload)
					.then((res) => {
						this.loading = false;
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},

		logout() {
			this.clearSession();
			try {
				navigator.sendBeacon(
					(import.meta.env.VITE_API_ENDPOINT || '/api/v1') +
						'/auths/logout',
				);
			} catch (err) {
				// noop — server session best-effort revoke
			}
			if (document.location.pathname != '/auths/login')
				document.location.href = '/auths/login';
		},
		clearSession() {
			this.token = null;
			this.version = null;
			this.permission = null;
			this.role = null;
			this.companies = null;
			this.company = null;
			this.branches = null;
			this.branch = null;
			this.division = null;
			this.data = null;
			localStorage.clear();
			sessionStorage.clear();
		},
		getPermission() {
			const response = useResponseStore();
			return new Promise((resolve, reject) => {
				$axios
					.get('/auths/permission')
					.then((res) => {
						if (res.data.success == true) {
							this.permission = res.data.data;
							response.setStorage('permissions', this.permission);
						}
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},
		setAccessToken(token) {
			this.token = token;
		},
		setSession(res) {
			const response = useResponseStore();
			this.token = res.data?.data?.token;
			const deviceId = res.data?.data?.deviceId ?? res.data?.data?.device_id;
			if (deviceId) $setStorage('device_id', deviceId);
			this.getMe().catch((err) => {
				throw err;
			});
		},
		async loadSession() {
			const response = useResponseStore();
			this.role = response.getStorage('roles');
			this.company = response.getStorage('company');
			this.companies = response.getStorage('companies');
			this.branch = response.getStorage('branch');
			this.branches = response.getStorage('branches');
			if (!this.token) {
				try {
					await refreshSession();
				} catch (err) {
					// refreshSession already hard-logsout on failure
				}
			}
			if (this.token) {
				await this.getMe().catch((err) => {
					throw err;
				});
			}
		},
		async getMe() {
			const response = useResponseStore();
			const module = useModuleStore();

			if (!getMePromise) {
				getMePromise = $axios
					.get('/auths/me')
					.then((res) => {
						if (res.data.success == true) {
							this.data = res.data.data;
							response.setStorage('data', this.data);
							this.permission = res.data.data.permissions;
							response.setStorage('permissions', this.permission);
							this.role = res.data.data.roleName;
							response.setStorage('roles', this.roles);
							if (module.check('company')) {
								this.branches = res.data.data.branches;
								this.companies = res.data.data.companies;
								response.setStorage('companies', this.companies);
								response.setStorage('branches', this.branches);

								if (this.branches?.length > 0) {
									const defaultBranch = this.branches.filter(
										(c) => c.isDefault == true,
									);
									if (defaultBranch?.length > 0) {
										this.branch = defaultBranch[0];
									} else {
										this.branch = this.branches[0];
									}
									if (this.companies?.length > 0) {
										const defaultCompany =
											this.companies.filter(
												(c) => c.isDefault == true,
											);
										if (defaultCompany?.length > 0) {
											this.company = defaultCompany[0];
										} else {
											this.company = this.companies[0];
										}
									} else {
										this.getCompany();
									}
								} else {
									this.getCompany();
								}
							}
						}
						return res.data;
					})
					.catch((err) => {
						throw err;
					})
					.finally(() => {
						getMePromise = null;
					});
			}
			return getMePromise;
		},
		getCompany() {
			const response = useResponseStore();
			const params = {
				skipPagination: true,
				isActive: true,
			};
			return new Promise((resolve, reject) => {
				$axios
					.get('auths/me/companies', { params: params })
					.then((res) => {
						if (res.data.success == true) {
							if (res.data.data.length > 0 && this.level >= 4) {
								this.company = res.data.data[0];
								response.setStorage('company', this.company);
							}
						}
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},
		getBranch() {
			const response = useResponseStore();
			const params = {
				company: this.company?.id,
				isActive: true,
			};
			return new Promise((resolve, reject) => {
				$axios
					.get('/auths/me/branches', { params: params })
					.then((res) => {
						if (res.data.success == true) {
							if (res.data.data.length > 0) {
								this.branches = res.data.data;
								this.branch = res.data.data[0];
								response.setStorage('branches', this.branches);
								response.setStorage('branch', this.branch);
							} else {
								this.branches = null;
								this.branch = null;
							}
						}
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},
		getDocumentation() {
			const response = useResponseStore();
			return new Promise((resolve, reject) => {
				$axios
					.get('/documentations')
					.then((res) => {
						if (res.data.success == true) {
							this.documentations = res.data.data;
						}
						resolve(res.data);
					})
					.catch((err) => {
						this.loading = false;
						reject(err);
					});
			});
		},
	},

	getters: {
		needChangePassword(state) {
			return state?.user?.needChangePassword;
		},
		authToken(state) {
			return state.token;
		},
		hasSession(state) {
			return state.token ? true : false;
		},
		user(state) {
			return state?.data;
		},
		roles(state) {
			return state?.user?.roleName;
		},
		level(state) {
			return state?.user?.roleLevel;
		},
		hasRoles: (state) => (roles) => {
			const can = state.roles
				? state.roles.find((val) => val === roles)
				: null;
			return can ? true : false;
		},
		permissions(state) {
			return state.permission;
		},
		can: (state) => (permission) => {
			const can = state.permission
				? state.permission.find((val) => val === permission)
				: null;
			return can ? true : false;
		},
	},
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
